import { hasPriceForCountry } from "@/utils/planPrices";
import { hasValidSubscriptionPayment } from "@/utils/subscriptionStatus";
import { useQuery } from "@apollo/client/react";
import { BasicModal } from "@repo/components/src/shared/modal";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { CurrencyCode } from "@repo/components/src/utils/money/currencies";
import { WorkspacePlanSettingsDocument } from "@repo/graphql/documents/workspace/query.generated";
import {
  SubscriptionsPlanCodeChoices,
  SubscriptionsQuotaCodeChoices,
} from "@repo/graphql/gql/graphql";
import React, { useMemo } from "react";
import PlanCard from "./planCard";

type Props = {
  billingCountryId: string | null;
  billingCurrencyCode: CurrencyCode | null;
  /** In-app path the checkout lands back on once it is paid. */
  returnPath: string;
};

/**
 * The plan cards of Settings -> Plan, in a modal, for a brand whose plan does
 * not include publishing campaigns: only plans that include it are offered.
 * Opened through `usePlanPicker`, which reads the workspace context this modal
 * is not mounted under. Picking a plan replaces this modal with the checkout
 * one; a workspace that already pays changes plan instead (`ChangePlan`),
 * which never comes back through `returnPath`, so it is not promised a return.
 */
const PlanPickerModal = React.memo((props: Props) => {
  const { billingCountryId, billingCurrencyCode, returnPath } = props;
  const { data, loading } = useQuery(WorkspacePlanSettingsDocument);
  const workspace = data?.workspace.myWorkspace;
  const plans = useMemo(
    () =>
      (data?.subscription.listPlans ?? []).filter(
        (plan) =>
          plan.planquotaSet.some(
            (planQuota) =>
              planQuota.quota.code.enum ===
                SubscriptionsQuotaCodeChoices.CreateCampaign &&
              planQuota.value > 0,
          ) &&
          (plan.code?.enum === SubscriptionsPlanCodeChoices.Enterprise ||
            hasPriceForCountry(plan, billingCountryId)),
      ),
    [data, billingCountryId],
  );
  const changesPlan = hasValidSubscriptionPayment(
    workspace?.workspace.workspaceplan.paymentrecurring?.status?.enum,
  );

  return (
    <BasicModal title="Elige un plan para publicar tu campaña">
      <VStack space="xs">
        <Text size="sm" action="muted">
          {changesPlan
            ? "Tu borrador está guardado. Cambia a un plan que incluya campañas para publicarlo."
            : "Tu borrador está guardado. Cuando termines el pago volverás aquí para publicarlo."}
        </Text>
        {billingCurrencyCode && (
          <Text size="xs" action="muted">
            Precios en {billingCurrencyCode}
          </Text>
        )}
        {workspace && !workspace.accountHolder && (
          <Text size="sm" action="warning">
            Solo el titular de la cuenta puede contratar un plan.
          </Text>
        )}
      </VStack>
      <Box className="mt-6">
        {loading || !workspace ? (
          <Box className="items-center py-12">
            <Spinner size="large" />
          </Box>
        ) : plans.length === 0 ? (
          <Text size="sm" action="muted" className="py-12 text-center">
            Planes no disponibles en tu país. Escríbenos para conocer las
            opciones disponibles.
          </Text>
        ) : (
          <HStack flexSpace="md">
            {plans.map((plan) => (
              <Box className="flex-1" key={plan.id}>
                <PlanCard
                  plan={plan}
                  workspace={workspace}
                  countryId={billingCountryId}
                  returnPath={returnPath}
                />
              </Box>
            ))}
          </HStack>
        )}
      </Box>
    </BasicModal>
  );
});

PlanPickerModal.displayName = "PlanPickerModal";

export default PlanPickerModal;
